import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import SignaturePad from "./SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NameInputs } from "./NameInputs";
import { toast } from "sonner";
import { ShieldCheck, FileText, PenLine, Upload } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function HrSign() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("draw"); // draw | upload | typed
  const [sig, setSig] = useState(null);
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    hrApi.get(`/public/link/${token}`).then((r) => { setInfo(r.data); setName((n) => n || r.data.employee_name || ""); if (r.data.status === "completed") setDone(true); })
      .catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  }, [token]);

  const onUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSig(reader.result);
    reader.readAsDataURL(file);
  };
  const submit = async () => {
    if (!name.trim()) return toast.error("Please type your first and last name");
    if (!ack) return toast.error("Please tick the acknowledgement");
    if ((mode === "draw" || mode === "upload") && !sig) return toast.error("Please add your signature");
    setBusy(true);
    try {
      await hrApi.post(`/public/contract/${token}/sign`, { typed_name: name.trim(), acknowledged: ack, signature_type: mode, signature_data: mode === "typed" ? null : sig });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  if (err) return <Shell><div className="text-center text-slate-500" data-testid="sign-invalid">{err}</div></Shell>;
  if (!info) return <Shell><div className="text-center text-slate-400">Loading…</div></Shell>;
  if (done) return <Shell><div className="text-center space-y-3" data-testid="sign-done">
    <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto"><ShieldCheck className="text-teal-600" size={28} /></div>
    <h1 className="font-heading font-extrabold text-2xl text-slate-800">Contract signed</h1>
    <p className="text-slate-500">Thank you{info.preferred_name || info.employee_name ? `, ${info.preferred_name || info.employee_name}` : ""}. A copy has been filed with HR.</p>
  </div></Shell>;

  return (
    <Shell>
      <div data-testid="sign-page" className="space-y-4">
        <div><h1 className="font-heading font-extrabold text-2xl text-slate-800">Your employment contract</h1>
          <p className="text-slate-500 text-sm">Please review and sign below, {info.preferred_name || info.employee_name}.</p></div>
        {info.has_file && <a data-testid="sign-view-file" href={`${BASE}/api/hr/public/contract/${token}/file`} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl border border-slate-200 p-3 text-indigo-600 font-semibold text-sm"><FileText size={18} /> View the contract document</a>}
        {info.contract_text && <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="sign-text">{info.contract_text}</div>}

        <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="flex gap-2">
            {[["draw", "Draw", PenLine], ["upload", "Upload", Upload], ["typed", "Type name", FileText]].map(([m, l, Icon]) => (
              <button key={m} data-testid={`sign-mode-${m}`} onClick={() => { setMode(m); setSig(null); }} className={`flex-1 py-2 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-1.5 border ${mode === m ? "bg-indigo-500 text-white border-indigo-500" : "border-slate-200 text-slate-600"}`}><Icon size={15} /> {l}</button>
            ))}
          </div>
          {mode === "draw" && <SignaturePad onChange={setSig} />}
          {mode === "upload" && <label data-testid="sign-upload" className="flex items-center justify-center gap-2 h-24 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 text-sm cursor-pointer">
            {sig ? <img src={sig} alt="signature" className="h-20" /> : <><Upload size={16} /> Upload a signature image</>}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onUpload(e.target.files[0])} /></label>}
          {mode === "typed" && <p className="text-xs text-slate-500">Your typed full name below will act as your signature.</p>}
        </div>

        <NameInputs value={name} onChange={setName} testid="sign-name" />
        <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="sign-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="w-4 h-4 mt-0.5 accent-indigo-500" /> I have read and agree to this contract, and this is my electronic signature.</label>
        <Button data-testid="sign-submit" onClick={submit} disabled={busy} className="w-full h-12 bg-indigo-500 hover:bg-indigo-400 text-white rounded-xl font-bold">{busy ? "Submitting…" : "Sign & submit"}</Button>
        <p className="text-[11px] text-slate-400 text-center">Your name, the time, and your IP address are recorded for the audit trail.</p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-100 py-8 px-4"><div className="max-w-lg mx-auto bg-white rounded-3xl shadow-sm p-6">{children}</div></div>;
}
